import { ref } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import messagingApi from '@/common/api/messaging.api';
import { useAuthStore } from '@/stores/auth';

/**
 * The client's conversations, in the patient portal (#5333).
 *
 * THE READ PATH IS SPLIT ACROSS TWO SOURCES, AND IT MUST STAY SPLIT. This is the same shape as
 * useMessageThread's split on the staff side, and collapsing it breaks security in a way no test
 * would catch:
 *
 * - **The conversation LIST comes from the server** (`GET /portal/account/{account}/messaging/threads`).
 *   It has to: the portal's one messaging grant is on `Communication`, and it holds nothing at all
 *   on `BN_MessageThread` or `BN_MessageThreadParticipant` — so a client physically cannot enumerate
 *   their own conversations, read who is in one, or tell an open one from a closed one.
 * - **The MESSAGES come straight from Aidbox**, under the client's own token, with BOTH `recipient`
 *   and `privateToStaff=false` on the search. `messaging-communication-read` requires both as
 *   literal matcho conditions, so dropping either matches no policy at all and Aidbox answers 403 —
 *   they are not optimizations. And the `recipient` predicate *is* the visibility rule: a message
 *   sent before the client joined the conversation is simply absent from its own recipient list, so
 *   there is no history to filter. Reading the bodies through the API instead would authenticate as
 *   the unrestricted `api-platform` client and hand over the entire thread, private staff messages
 *   included, while looking perfectly correct.
 *
 * State is module-scoped, following useMessageInbox: the portal nav's unread indicator and the
 * messages page read the same list, and per-instance refs would let the badge and the page disagree
 * about what has been read.
 */

const threads = ref([]);
const messages = ref([]);
const activeThreadId = ref('');
const loading = ref(false);
const error = ref('');

/**
 * How many messages one read of a conversation returns.
 *
 * `_sort=sent` is ASCENDING in FHIR — a bare field name always is — so asking for it returned the
 * OLDEST window of a long conversation, with no paging and nothing on screen saying so. `-sent`
 * takes the newest instead, and the rows are flipped back into reading order below.
 */
const MESSAGE_WINDOW = 500;

/** True when the conversation has more messages than one window can hold. */
const hasOlderMessages = ref(false);

/**
 * Drop the loaded conversations on sign-out and account switch.
 *
 * Module scope outlives the components, and the portal signs out without a reload — so on a shared
 * family device the next person to sign in would otherwise see the previous one's conversations
 * until a later read replaced them.
 */
export function resetPortalMessages() {
    threads.value = [];
    messages.value = [];
    activeThreadId.value = '';
    loading.value = false;
    error.value = '';
    hasOlderMessages.value = false;
}

export function usePortalMessages() {
    const authStore = useAuthStore();

    /**
     * The signed-in client's own Patient id — what `recipient` has to match.
     *
     * Deliberately `patient`, never `portalViewingPatient`. A guardian browsing a child's chart is
     * still reading their own messages: the access policy compares `recipient` to
     * `.user.portalUser.id`, which is the person who signed in, so a viewing-patient switch here
     * would produce a search matching no policy at all. Conversations are not shared across a
     * guardian relationship, by design.
     */
    const ownPatientId = () => authStore.patient?.id || '';

    // `payload[0].content.string` in the stored (Aidbox FCE) shape, `contentString` once the
    // fhirworks ContentX accessor has wrapped it. Read both rather than betting on which layer ran.
    const messageBody = (resource) => {
        const content = resource?.payload?.[0]?.content;
        return content?.string ?? content?.contentString ?? '';
    };

    /**
     * Both response shapes are tolerated on purpose: the FHIR interceptor turns a non-empty Bundle
     * into an array of fhirworks resources on `data`, but leaves an empty Bundle as the raw object.
     */
    const extractResources = (result) => {
        if (Array.isArray(result?.data)) return result.data;
        const entries = result?.originalData?.entry || result?.data?.entry || [];
        return entries.map((entry) => entry?.resource ?? entry).filter(Boolean);
    };

    const normalizeMessage = (resource) => ({
        id: resource?.id,
        body: messageBody(resource),
        senderId: resource?.sender?.id ?? null,
        sent: resource?.sent ?? null,
        // Whether the client sent it. `sender.resourceType` rather than an id comparison, because a
        // client conversation has exactly one Patient in it — the client — so the type answers it
        // without depending on the id having loaded.
        fromClient: resource?.sender?.resourceType === 'Patient',
    });

    const oldestFirst = (rows) => [...rows].sort((a, b) => String(a.sent || '').localeCompare(String(b.sent || '')));

    /** The conversation list, from the server. Never message bodies — see the composable docblock. */
    const loadThreads = async () => {
        loading.value = true;
        error.value = '';
        try {
            const result = await messagingApi.getPortalThreads();
            threads.value = Array.isArray(result?.threads) ? result.threads : [];
        } catch (err) {
            error.value = 'Your messages could not be loaded.';
            console.error('usePortalMessages: failed to load the conversation list', err);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Mark a conversation read.
     *
     * Applied to the local row optimistically so the nav indicator clears immediately, and rolled
     * back on failure — an indicator that wrongly persists is a smaller problem than one that
     * wrongly clears and hides a message the client never opened.
     */
    const markRead = async (threadId) => {
        const thread = threads.value.find((candidate) => candidate.threadId === threadId);
        const previousUnread = thread?.unread;

        if (thread) thread.unread = false;

        try {
            await messagingApi.markPortalThreadRead(threadId);
        } catch (err) {
            if (thread) thread.unread = previousUnread;
            console.error('usePortalMessages: failed to mark the conversation read', threadId, err);
        }
    };

    /**
     * Open one conversation: read its messages, then mark it read.
     *
     * Marking read is part of opening rather than a separate action the page has to remember — the
     * client has seen the messages by the time they are on screen. It is also how the live path
     * refreshes: an arriving message re-opens the conversation that is already on screen, which
     * re-reads through the gated path and re-marks it read.
     */
    const openThread = async (threadId) => {
        if (!threadId) return;

        activeThreadId.value = threadId;
        loading.value = true;
        error.value = '';

        try {
            // BOTH parameters are mandatory. `messaging-communication-read` names them as literal
            // conditions, so a search missing either matches no policy and is denied outright.
            const result = await $httpFhirApi.get(
                `/Communication?thread=${encodeURIComponent(threadId)}&recipient=${encodeURIComponent(ownPatientId())}&privateToStaff=false&_sort=-sent&_count=${MESSAGE_WINDOW}`,
            );
            const rows = extractResources(result).map(normalizeMessage);
            hasOlderMessages.value = rows.length >= MESSAGE_WINDOW;
            messages.value = oldestFirst(rows);
        } catch (err) {
            // Never a partial render: a half-loaded conversation reads as "these are all the
            // messages", which is worse than an error the reader can act on.
            messages.value = [];
            error.value = 'This conversation could not be loaded.';
            console.error('usePortalMessages: failed to load the conversation', threadId, err);
            return;
        } finally {
            loading.value = false;
        }

        await markRead(threadId);
    };

    /**
     * Reply in the open conversation.
     *
     * The created message is appended from the server's response rather than optimistically from the
     * draft, so what is on screen is what was actually stored. On failure the list is left exactly as
     * it was — a message that failed to send must not look as though it arrived.
     */
    const send = async (body) => {
        const trimmed = (body || '').trim();
        // Refused before the request: an empty message is not a server round trip.
        if (!trimmed || !activeThreadId.value) return null;

        try {
            error.value = '';
            const created = await messagingApi.sendPortalMessage({ threadId: activeThreadId.value, body: trimmed });
            const message = normalizeMessage(created);
            messages.value = [...messages.value, message];
            return message;
        } catch (err) {
            error.value = 'Your message could not be sent.';
            console.error('usePortalMessages: failed to send the message', activeThreadId.value, err);
            return null;
        }
    };

    return { threads, messages, hasOlderMessages, activeThreadId, loading, error, loadThreads, openThread, send, markRead };
}

export default usePortalMessages;
